<template>
    <transition name="fade">
        <div v-if="showModal" class="set-school-modal">
            <div class="modal-backdrop p-4"></div>
            <form @submit.prevent="form.thirdParty ? submitWith3rdParty() : submitSchool()" class="modal-content">
                <InnerLoader v-if="isLoading" />
                <div class="modal-header">
                    <h1 class="shadow-text-none">{{ $t('set_school_header') }}</h1>
                </div>
                <div class="modal-body">
                    <div class="form-control flex flex-col w-full">
                        <label for="set-school-form-email">{{ $t('set_school_label_email') }}</label>
                        <input
                            v-model="form.email"
                            v-validate="'required|email'"
                            :placeholder="$t('set_school_placeholder_email')"
                            id="set-school-form-email"
                            type="text"
                            name="email"
                            disabled
                        />
                        <span v-if="errors.has('email')" class="has-error font-size-14 text-red shadow-text-none">
                            {{ $t('set_school_invalid_email') }}
                        </span>
                    </div>
                    <div class="form-control flex flex-col w-full">
                        <label for="set-school-form-school">{{ $t('set_school_label_school') }}</label>
                        <input
                            v-model="form.school_name"
                            v-validate="'required'"
                            :placeholder="$t('set_school_placeholder_school')"
                            type="text"
                            id="set-school-form-school"
                            name="school-name"
                        />
                        <span v-if="errors.has('school-name')" class="has-error font-size-14 text-red shadow-text-none">
                            {{ $t('set_school_invalid_school') }}
                        </span>
                    </div>
                    <div class="form-control flex flex-col w-full">
                        <label for="set-school-form-district">{{ $t('set_school_label_district') }}</label>
                        <input
                            v-model="form.school_district"
                            v-validate="'required'"
                            :placeholder="$t('set_school_placeholder_district')"
                            type="text"
                            id="set-school-form-district"
                            name="district"
                        />
                        <span v-if="errors.has('district')" class="has-error font-size-14 text-red shadow-text-none">
                            {{ $t('set_school_invalid_district') }}
                        </span>
                    </div>
                    <div class="form-control flex flex-col w-full">
                        <label for="set-school-form-country">{{ $t('set_school_label_country') }}</label>
                        <CountrySelect
                            @selected="onSelectedCountry($event)"
                            id="set-school-form-country"
                            ref="countrySelect"
                        />
                        <span v-if="invalidCountry" class="has-error font-size-14 text-red shadow-text-none">
                            {{ $t('set_school_invalid_country') }}
                        </span>
                    </div>
                    <div class="form-control flex flex-col w-full">
                        <label for="set-school-form-phone">{{ $t('set_school_label_phone') }}</label>
                        <input
                            v-model="form.school_phone"
                            v-validate="'required|min:8|max:10'"
                            @input="inputNumber"
                            :placeholder="$t('set_school_placeholder_phone')"
                            id="set-school-form-phone"
                            type="text"
                            maxlength="10"
                            minlength="8"
                            name="phone"
                        />
                        <span v-if="errors.has('phone')" class="has-error font-size-14 text-red shadow-text-none">
                            {{ $t('set_school_invalid_phone') }}
                        </span>
                    </div>
                </div>
                <div class="modal-footer">
                    <XButton
                        :disabled="isLoading"
                        bg-color="var(--button-background-color)"
                        color="var(--button-text-color)"
                        type="submit"
                    >
                        {{ $t('auth_login_button_register_teacher') }}
                    </XButton>
                </div>
            </form>
        </div>
    </transition>
</template>

<script>
import { mapState } from 'vuex'
import appConfig from '@/app.config.js'
import Request from '@/lib/request.js'
import { load } from 'recaptcha-v3'
import popup from '@/lib/popup.js'
import helper from '@/lib/helper'
import InnerLoader from '@/components/InnerLoader'
import XButton from '@/components/v2/XButton'
import CountrySelect from '@/components/CountrySelect'
import datalist from '@/mixins/datalist'

export default {
    mixins: [datalist],

    components: {
        InnerLoader,
        XButton,
        CountrySelect
    },

    props: {
        formSetSchool: {
            type: Object,
            default: () => {}
        }
    },

    data() {
        return {
            showModal: true,
            isLoading: false,
            firstSubmit: false,
            invalidCountry: false,
            form: {
                email: '',
                school_name: '',
                school_district: '',
                school_country: '',
                school_country_name: '',
                school_phone: ''
            }
        }
    },

    mounted() {
        this.form = {
            ...this.form,
            ...this.formSetSchool
        }

        if (this.geoLocation && this.isInCountriesList && this.$refs.countrySelect) {
            this.$refs.countrySelect.selectedCountry = {
                name: this.geoLocation.name,
                code: this.geoLocation.code
            }
        }
    },

    computed: {
        ...mapState({
            geoLocation: state => state.geoLocation
        }),

        isInCountriesList() {
            return this.countriesList.some(
                country => country.code === this.geoLocation.code && country.name === this.geoLocation.name
            )
        }
    },

    methods: {
        openModal() {
            this.showModal = true
        },

        inputNumber(number) {
            const regex = /[^0-9]+/g
            const val = number.target.value

            number.target.value = val.replace(val.match(regex), '')
            this.form.school_phone = number.target.value
        },

        onSelectedCountry(country) {
            if (!country) {
                this.form.school_country = ''
                this.form.school_country_name = ''
                return
            }

            this.form.school_country = country.code
            this.form.school_country_name = country.name
        },

        async validateForm() {
            const validateResult = await this.$validator.validate()

            if (!validateResult || !this.form.school_country) {
                if (!this.form.school_country) {
                    this.invalidCountry = true
                }

                return false
            }

            this.invalidCountry = false

            return true
        },

        getRecaptchaToken() {
            return new Promise(async (resolve, reject) => {
                try {
                    const recaptcha = await load(process.env.VUE_APP_RECAPTCHA_SITE_KEY, {
                        useRecaptchaNet: true,
                        autoHideBadge: true
                    })

                    const token = await recaptcha.execute('validate_captcha', {
                        action: 'validate_captcha'
                    })

                    resolve(token)
                } catch (err) {
                    reject(err)
                }
            })
        },

        async submitSchool() {
            this.firstSubmit = true

            const validateResult = await this.validateForm()

            if (!validateResult) return

            this.isLoading = true

            try {
                const payload = {
                    ...this.form,
                    captcha_response: await this.getRecaptchaToken(),
                    create_from: window.location.pathname,
                    email: this.formSetSchool.email,
                    school_slug: this.slugify(this.form.school_country_name)
                }

                this.$emit('register-otp', payload)
            } catch (err) {
                const res = err.response.data.data

                this.isLoading = false

                if (res.email) {
                    popup.alert(this.$t('auth_alert_already_email'))
                }
            }
        },

        async submitWith3rdParty() {
            this.firstSubmit = true

            const validateResult = await this.validateForm()

            if (!validateResult) return

            this.isLoading = true

            try {
                const payload = {
                    ...this.form,
                    email: this.formSetSchool.email,
                    school_slug: this.slugify(this.form.school_country_name)
                }

                const request = new Request()

                const response = await request.post(appConfig.api.login3rd, payload)

                if (response.data.is_created) {
                    helper.gTrackEvent('register', {
                        event_category: 'Register',
                        event_label: this.form.provider
                    })
                }

                this.$emit('register-success', response.data)
                this.$emit('close')
            } catch (err) {
                const res = err.response

                if (typeof res.data.data.status != 'undefined') {
                    return popup.alert(
                        res.data.data.status == 'suspended' ? this.translate('auth_login_alert_lock_account') : ''
                    )
                }
            } finally {
                this.isLoading = false
            }
        },

        slugify(text) {
            return text
                .toString()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase()
                .trim()
                .replace(/\s+/g, '-')
                .replace(/[^\w-]+/g, '')
                .replace(/--+/g, '-')
        }
    }
}
</script>

<style lang="scss">
.set-school-modal {
    position: fixed;
    top: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;

    .modal-backdrop {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.2);
        z-index: 0 !important;
    }

    .modal-content {
        position: relative;
        max-width: 400px;
        width: 100%;
        background-color: #fff;
        border-radius: 6px;

        .inner-loader {
            border-radius: 6px;
        }

        .modal-header {
            padding: 14px 14px 0 14px;

            h1 {
                font-size: 17.5px;
                margin: 0;
            }
        }

        .modal-body {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            row-gap: 10.5px;
            padding: 14px;

            .form-control {
                row-gap: 7px;

                label {
                    font-size: 14px;
                    font-weight: bold;
                }

                input:not(.vs__search) {
                    height: 40px;
                    border-radius: 6px;
                    padding: 10.5px;
                    border: 1px solid #ced4da;
                    font-size: 14px;
                    color: #495057;
                }
            }
        }

        .modal-footer {
            padding: 0 14px 14px 14px;
            margin-top: 14px;

            button {
                width: 100%;
                border-radius: 6px;
                font-size: 14px;
            }
        }
    }
}
</style>